/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnInit,
} from '@angular/core';
import { FeedStoreService } from '../services/feed-store.service';
import { createFailureAwareStore } from '../shared/failure-aware-store';
import { focusNewItem } from '../shared/focus-new-item';
import {
  SearchMode, SearchTab, TYPE_MAP, resolveTab, searchTabs,
} from './search-tabs';

const MODES: Record<string, SearchMode> = {
  searchresult: 'query',
  searchresultbytag: 'tag',
  searchresultbyhashtag: 'hashtag',
};

const SORTS = { newest: '-published_at', oldest: 'published_at' };

// The search results pages (query, tag and hashtag) in the 2026 design, routed by UTEST_DESIGN_REFRESH_2026 (see
// SEARCH_UTILITY_REDESIGN.html). Same URLs, tabs, sort and API calls as the legacy searchResult component; results
// are community feed cards (people get a person card) with the search terms marked. Each tab and sort is its own
// URL, so changing one is a new transition and a fresh component.
@Component({
  selector: 'ut-search-page',
  templateUrl: './search-page.component.html',
  styleUrls: ['./search-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SearchPageComponent implements OnInit {
  // The state's `user` resolve; null when signed out.
  @Input() user: any;

  mode: SearchMode = 'query';
  query = '';
  tab = 'all';
  tabs: SearchTab[] = [];
  sort: keyof typeof SORTS = 'newest';
  // From the X-Total header when the API sends it.
  total: number | null = null;
  store: any;
  private current: Promise<any> = Promise.resolve();
  private reloadQueued = false;

  constructor(
    @Inject('SearchService') private SearchService: any,
    @Inject('$state') private $state: any,
    @Inject('$location') private $location: any,
    private feedStore: FeedStoreService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    const { params } = this.$state;
    this.mode = MODES[this.$state.current.name] || 'query';
    this.query = (params.query || '').trim();
    this.tabs = searchTabs(this.mode, !!this.user);
    this.tab = resolveTab(params.contentType === 'courses' ? 'all' : params.contentType, this.tabs);
    // ?sort isn't a state param, as on the legacy page, so changing it doesn't start a transition.
    this.sort = this.$location.search().sort === 'oldest' ? 'oldest' : 'newest';
    if (!this.query) return;

    const search = this.tab === 'all'
      ? (p) => this.SearchService.searchMixed(p)
      : this.SearchService.searchOneFactory(TYPE_MAP[this.tab], this.mode === 'tag');
    this.store = createFailureAwareStore(this.feedStore, (p) => search(p).then((res) => {
      const total = res.headers?.('X-Total');
      this.total = total ? Number(total) : null;
      return res;
    }), { query: this.mode === 'hashtag' ? `#${this.query}` : this.query, sort: SORTS[this.sort] }, { toast: false });
    this.track(this.store.load());
  }

  get stateName(): string {
    return this.$state.current.name;
  }

  get title(): string {
    if (!this.query) return 'Search results';
    return `Search results for “${this.mode === 'hashtag' ? '#' : ''}${this.query}”`;
  }

  get sortable(): boolean {
    return this.tab !== 'users';
  }

  // Courses have their own catalog and are left out, as before.
  get results(): any[] {
    return this.store ? this.store.feedItems.filter((item) => item.type !== 'course') : [];
  }

  get status(): string {
    if (!this.store) return '';
    if (this.store.failed) return '';
    if (this.store.isLoading) return 'Searching';
    const n = this.results.length;
    return `${n} ${n === 1 ? 'result' : 'results'} shown`;
  }

  get emptyTitle(): string {
    return `No results for “${this.mode === 'hashtag' ? '#' : ''}${this.query}”`;
  }

  resultId(item: any): string {
    return `result_${item.type}_${item.id}`;
  }

  selectTab(contentType: string): void {
    this.$state.go(this.stateName, { contentType });
  }

  setSort(sort: string): void {
    this.sort = sort === 'oldest' ? 'oldest' : 'newest';
    this.$location.search('sort', this.sort);
    this.store.parameters.sort = SORTS[this.sort];
    if (this.reloadQueued) return;
    this.reloadQueued = true;
    this.track(this.current.then(() => {
      this.reloadQueued = false;
      return this.store.load();
    }));
  }

  retry(): void {
    this.fetchNext(() => (this.store.feedItems.length ? this.store.loadMore() : this.store.load()));
  }

  loadMore(): void {
    this.fetchNext(() => this.store.loadMore());
  }

  // Focus follows the list: the first new result when the page loaded, Retry again when it failed.
  private fetchNext(fetch: () => Promise<any>): void {
    const next = this.results.length;
    this.track(fetch()).then(() => {
      const item = this.results[next];
      if (this.store.failed) focusNewItem(this.el.nativeElement, '.alert--error button');
      else if (item) focusNewItem(this.el.nativeElement, `#${this.resultId(item)} > article`);
    });
  }

  private track(promise: Promise<any>): Promise<any> {
    this.cdr.markForCheck();
    this.current = Promise.resolve(promise).then(() => this.cdr.markForCheck());
    return this.current;
  }
}
